import { useState, useRef, useCallback, useEffect } from 'react';

interface UseSpeechToTextOptions {
  onResult?: (transcript: string) => void;
  onError?: (error: string) => void;
  onStart?: () => void;
  onEnd?: () => void;
  lang?: string;
}

// Type declarations for Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

export const useSpeechToText = (options: UseSpeechToTextOptions = {}) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isPaused, setIsPaused] = useState(false); // Track if paused for TTS
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const shouldResumeRef = useRef(false); // Track if we should auto-resume after TTS

  // Check for browser support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setIsSupported(!!SpeechRecognition);

    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.lang = options.lang || 'en-US';

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        const result = event.results[event.resultIndex];
        if (result.isFinal) {
          const finalTranscript = result[0].transcript;
          setTranscript(finalTranscript);
          options.onResult?.(finalTranscript);
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        setIsPaused(false);
        
        let errorMessage = 'Speech recognition failed';
        switch (event.error) {
          case 'no-speech':
            errorMessage = 'No speech detected. Tap mic and try again.';
            break;
          case 'audio-capture':
            errorMessage = 'No microphone found. Please check your device.';
            break;
          case 'not-allowed':
            errorMessage = 'Microphone access denied. Please allow microphone access.';
            break;
          case 'network':
            errorMessage = 'Network error. Please check your connection.';
            break;
          case 'aborted':
            // User aborted or paused for TTS, no error message needed
            return;
          default:
            errorMessage = `Error: ${event.error}`;
        }
        
        options.onError?.(errorMessage);
      };

      recognition.onstart = () => {
        // Cancel any ongoing browser TTS when user starts speaking
        if (window.speechSynthesis) {
          window.speechSynthesis.cancel();
        }
        setIsListening(true);
        setIsPaused(false);
        options.onStart?.();
      };

      recognition.onend = () => {
        setIsListening(false);
        options.onEnd?.();
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [options.lang]);

  const startListening = useCallback(() => {
    if (!recognitionRef.current) {
      options.onError?.('Speech recognition not supported in this browser');
      return;
    }

    try {
      setTranscript('');
      setIsPaused(false);
      recognitionRef.current.start();
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      options.onError?.('Failed to start listening. Please try again.');
    }
  }, [options]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  }, [isListening]);

  const abortListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      setIsListening(false);
      setIsPaused(false);
    }
  }, []);

  // Pause mic for TTS - call this when TTS starts
  const pauseForTTS = useCallback(() => {
    if (recognitionRef.current && isListening) {
      console.log('[STT] Pausing mic for TTS');
      shouldResumeRef.current = true;
      recognitionRef.current.stop();
      setIsPaused(true);
    }
  }, [isListening]);

  // Resume mic after TTS - call this when TTS ends
  const resumeAfterTTS = useCallback(() => {
    if (recognitionRef.current && shouldResumeRef.current) {
      console.log('[STT] Resuming mic after TTS');
      shouldResumeRef.current = false;
      setIsPaused(false);
      try {
        setTranscript('');
        recognitionRef.current.start();
      } catch (error) {
        console.error('Failed to resume speech recognition:', error);
      }
    }
  }, []);

  return {
    isListening,
    isSupported,
    transcript,
    isPaused,
    startListening,
    stopListening,
    abortListening,
    pauseForTTS,
    resumeAfterTTS,
  };
};
